import {
    first,
    timeout as rxTimeout,
    share,
    catchError,
    retry,
    map,
    filter,
    bufferTime
} from 'rxjs/operators';
import {
    isEmpty as _isEmpty,
    groupBy as _groupBy,
    maxBy as _maxBy,
    map as _map,
} from '@lodash';
import { TimeoutError, Observable, identity } from 'rxjs';
import Zip from '@utils/dataTransformation/Zip';
import OfferRequest from '@core/services/offer/_private/models/OfferRequest';
import EventMapper from '@core/mappers/offer/EventMapper';
import IEventDto from '@core/mappers/offer/dtos/IEventDto';
import Match from '@core/models/offer/Match';
import Logger from '@loggers/Logger';
import { IStructManager } from '@core/services/common/StructManager';
import ErrorType from '@errors/network/ErrorType';
import { RequestError } from '@errors/network/RequestError';
import { SOCKET_BUFFER_TIME, SOCKET_RETRY_N_TIMES_ON_ERROR } from '@core/constants';
import { ISuperOfferDto } from '@core/mappers/offer/dtos/ISuperOfferDto';
import SuperOffer from '@core/models/offer/SuperOffer';
import SuperOfferMapper from '@core/mappers/offer/SuperOfferMapper';
import MaintenanceService from '@core/services/maintenance/MaintenanceService';
import EventSubscriptionCounter from '@core/services/offer/_private/helpers/EventSubscriptionCounter';
// import { logSocketEvent, logSocketRawEvent } from '@utils/network/NetworkUtils';

interface Streams {
    allEventChanges$: Observable<any>;
    data$: Observable<any>;
    liveEventsCountChanges$: Observable<any>;
}

export default class EventStreams {
    public eventMapper: EventMapper = new EventMapper();
    public superOfferMapper: SuperOfferMapper = new SuperOfferMapper();
    public logger = Logger;
    private structManager: IStructManager;
    private allEventChanges$: Observable<any>;
    private liveEventsCountChanges$: Observable<any>;
    private data$: Observable<any>;
    private eventSubscriptionCounter: EventSubscriptionCounter;

    constructor(
        structManager: IStructManager,
        { allEventChanges$, data$, liveEventsCountChanges$ }: Streams,
        eventSubscriptionCounter: EventSubscriptionCounter
    ) {
        this.structManager = structManager;
        this.data$ = data$;
        this.liveEventsCountChanges$ = liveEventsCountChanges$;
        this.eventSubscriptionCounter = eventSubscriptionCounter;

        this.allEventChanges$ = allEventChanges$.pipe(
            map(this.unzipIfCompress),
            map(this.toEvent),
            map(this.denormalizeEvent),
            retry(SOCKET_RETRY_N_TIMES_ON_ERROR),
            catchError(this.logAndRethrow),
            share());
    }

    public getData$(offerRequest: OfferRequest, timeout: number, removeEventsInMaintenance = true): Observable<any> {
        return this.data$.pipe(
            rxTimeout(timeout),
            map(this.unzipIfCompress),
            map(this.throwIfDataUnsuccessful),
            filter(this.filterRequest(offerRequest)),
            first(this.isMyRequest(offerRequest)),
            map(this.extractResponseData),
            map(this.toEvents),
            map(this.denormalizeEvents),
            map(removeEventsInMaintenance ? this.removeEventsInMaintenance : identity),
            catchError(this.logAndRethrow));
    }

    public getSuperOfferData$(offerRequest: OfferRequest) {
        return this.data$.pipe(
            map(this.unzipIfCompress),
            map(this.throwIfDataUnsuccessful),
            filter(this.filterRequest(offerRequest)),
            map(this.extractResponseData),
            map(this.denormalizeSuperOffer(this.superOfferMapper)),
            catchError(this.logAndRethrow));
    }

    public getChanges$(eventIds: number[], removeEventsInMaintenance = true): Observable<any> {
        return this.allEventChanges$.pipe(
            filter(this.in(eventIds)),
            filter(this.containsFullOddList),
            bufferTime(SOCKET_BUFFER_TIME),
            filter(this.isFullBuffer),
            map(removeEventsInMaintenance ? this.removeEventsInMaintenance : identity),
            map(this.newest));
    }

    public getAllChanges$(removeEventsInMaintenance = true): Observable<any> {
        return this.allEventChanges$.pipe(
            filter((match: Match) => {
                const isSubscribedToFull = this.eventSubscriptionCounter.hasSubscriptions(match.id);
                (match as any).getAllChanges_isSubscribedToFull = isSubscribedToFull;
                (match as any).getAllChanges_cntFullOdds = match.containsFullOddList();
                // logSocketEvent('getAllChanges', match);
                return !isSubscribedToFull || isSubscribedToFull && match.containsFullOddList();
            }),
            bufferTime(SOCKET_BUFFER_TIME),
            filter(this.isFullBuffer),
            map(removeEventsInMaintenance ? this.removeEventsInMaintenance : identity));
    }

    public getLiveEventCountChanges$(): Observable<any> {
        return this.liveEventsCountChanges$
            .pipe(map(this.extractLiveEventCount));
    }

    public unzipIfCompress = (data: any): any => {
        if (data instanceof ArrayBuffer) {
            return this.unzip(data);
        }
        /*
            Some methods now return [data, requestType]. More will transition to this response type
            in the future. In the transitional period we will support both ways.
        */
        if (data.constructor === Array && data[0] instanceof ArrayBuffer) {
            return this.unzip(data[0]);
        }
        return data;
    };

    public isMyRequest(offerRequest: OfferRequest): (data: any) => boolean {
        return (data: any) => JSON.stringify(data.dataIn) === offerRequest.toString();
    }

    public throwIfDataUnsuccessful(data: any): any {
        if (!data.error) {
            return data;
        }

        throw new Error('Data success status is false');
    }

    public toEvent = (event: IEventDto): Match => {
        // logSocketRawEvent('toEvent', event);
        return this.eventMapper.map(event);
    };

    public toEvents = (data: IEventDto[]): Match[] => {
        return this.eventMapper.map(data, true);
    };

    public logAndRethrow = (error: any, source: any): any => {
        this.logger.error(error);
        throw error;
    };

    public denormalizeEvent = (event: Match): Match => {
        // logSocketEvent('denormalizeEvent', event);
        this.structManager.denormalizeEvent(event);
        return event;
    };

    public denormalizeEvents = (events: Match[]): Match[] => {
        this.structManager.denormalizeEvents(events);
        return events;
    };

    public removeEventsInMaintenance = (events: Match[]): Match[] =>
        events.filter(e => {
            // logSocketEvent('removeEventsInMaintenance', e);
            return !MaintenanceService.getInstance().isEventInMaintenance(e);
        });

    public transformToAppErrors(error: any): Error {
        if (error instanceof TimeoutError) {
            return new RequestError(error, ErrorType.timeout);
        }

        return error;
    }

    public unzip(data: any): void {
        return Zip.unzip(data);
    }

    public extractResponseData(response: any): any {
        return response.data;
    }

    denormalizeSuperOffer(superOfferMapper: SuperOfferMapper) {
        return (superOfferDto: ISuperOfferDto): SuperOffer => {
            const superOffer = superOfferMapper.map(superOfferDto);
            superOffer.superExtra = this.removeEventsInMaintenance(this.denormalizeEvents(superOffer.superExtra));
            superOffer.superKvota = this.removeEventsInMaintenance(this.denormalizeEvents(superOffer.superKvota));
            return superOffer;
        };
    }

    public filterRequest(offerRequest: OfferRequest) {
        return (data: any) => data.dataIn.method === offerRequest.method;
    }

    public isFullBuffer(events: Match[]): boolean {
        return !_isEmpty(events);
    }

    public containsFullOddList(event: Match): boolean {
        // logSocketEvent('containsFullOddList', event);
        return event.containsFullOddList();
    }

    public newest(events: Match[]) {
        const grouped = _groupBy(events, 'id');
        return _map(grouped, (events) => _maxBy(events, 'incrementId'));
    }

    public in(eventIds: number[]): (event: Match) => boolean {
        const eventIdsSet = new Set(eventIds);
        return (event: Match) => {
            // logSocketEvent('in', event);
            return eventIdsSet.has(event.id);
        };
    }

    public extractLiveEventCount([liveEventCount]: [number, string]): number {
        return liveEventCount;
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--12!./src/core/services/offer/_private/helpers/EventStreams.ts